import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { LandingBackgroundMedia } from './LandingBackgroundMedia';
import { buildVerticalPath, getVerticalAppStyles, type VerticalType } from '../../config/vertical';

interface LandingHeroSectionProps {
  vertical: VerticalType;
  heroTitle: string;
  unitLabel: string;
  sectionEntityTitle: string;
  heroVideo: string;
  heroPoster?: string;
  heroImage?: string;
  isGreenhouse: boolean;
  isOrchard: boolean;
  greenhouseHeroFallback: string;
  orchardHeroFallback: string;
}

const LandingHeroSection: React.FC<LandingHeroSectionProps> = ({
  vertical,
  heroTitle,
  unitLabel,
  sectionEntityTitle,
  heroVideo,
  heroPoster,
  heroImage,
  isGreenhouse,
  isOrchard,
  greenhouseHeroFallback,
  orchardHeroFallback,
}) => {
  const styles = getVerticalAppStyles(vertical);
  return (
    <section className="relative min-h-[28rem] bg-white overflow-hidden py-6 sm:py-8 md:py-10 flex flex-col justify-center">
      {isGreenhouse ? (
        <LandingBackgroundMedia
          fallbackSrc={heroImage || greenhouseHeroFallback}
          alt="Greenhouse flyover"
          overlay="subtle"
          lazy={false}
        />
      ) : isOrchard ? (
        <LandingBackgroundMedia
          fallbackSrc={heroImage || orchardHeroFallback}
          alt="Orchard flyover"
          overlay="subtle"
          lazy={false}
        />
      ) : (
        <LandingBackgroundMedia
          videoSrc={heroVideo}
          posterSrc={heroPoster}
          alt={`${sectionEntityTitle} flyover`}
          overlay="subtle"
          lazy={false}
        />
      )}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="frosted-panel text-center max-w-6xl mx-auto p-6 sm:p-8">
          <h1 className="text-4xl sm:text-5xl tracking-tight font-extrabold text-wood-900 md:text-6xl lg:text-7xl">
            {heroTitle}
          </h1>
          <p className="mt-2 max-w-3xl mx-auto text-lg text-wood-800 sm:text-xl">
            Track labor costs, manage compliance, and keep field work organized by {unitLabel} so you know exactly what is working and what is not.
          </p>

          <div className="mt-4 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={buildVerticalPath(vertical, '/signup')} className={styles.ctaPrimary}>
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link to={buildVerticalPath(vertical, '/login')} className={styles.ctaSecondary}>
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingHeroSection;
